<template>
  <div class="tenant-gone">
    <div class="tenant-gone__card">
      <LumiIcon name="error" class="tenant-gone__icon" />
      <!-- A person whose own membership is gone (a revoked demo guest, an ended
           demo) is told so, in words of its own; every other cause keeps the one
           neutral sentence below — see the component comment. -->
      <h1 v-if="accessEnded" class="tenant-gone__title" data-test="tenant-access-ended">{{ t('tenantUnavailable.ended.title') }}</h1>
      <h1 v-else class="tenant-gone__title">{{ t('tenantUnavailable.title') }}</h1>
      <!-- ONE sentence for BOTH causes. It must read the same whether you
           mistyped an address or your employer's workspace was suspended — see
           the component comment. -->
      <p class="tenant-gone__body">{{ accessEnded ? t('tenantUnavailable.ended.body') : t('tenantUnavailable.body') }}</p>

      <!-- The way out that still works while the workspace does not. Rendered
           only when a host actually resolved: a dead link here would be worse
           than none, and this is the one address the page exists to hand over. -->
      <template v-if="accountHref">
        <p class="tenant-gone__lead">{{ accessEnded ? t('tenantUnavailable.ended.accountLead') : t('tenantUnavailable.accountLead') }}</p>
        <a class="tenant-gone__cta" :href="accountHref" rel="noopener">{{ t('tenantUnavailable.accountCta') }}</a>
      </template>

      <p class="tenant-gone__lead">{{ t('tenantUnavailable.supportLead') }}</p>
      <a class="tenant-gone__link" :href="supportMailto">{{ t('common.contactSupport') }}</a>
      <!-- The address itself, printed (issue 364, P2). The line above is a label
           over a `mailto:` — on a page reached without a session, in a browser with
           no mail client wired up, that control does nothing visible and reads as
           dead. Whoever cannot get in has to be able to SEE the address to write
           from their phone, and to quote it. Same treatment as workspace-ui's
           WorkspaceLocked, the other page this person may land on. Not copy, so
           not a translation key: it is Lumi's own mailbox, identical in every
           locale, and it says nothing about any workspace. -->
      <a class="tenant-gone__email" :href="supportMailto">{{ SUPPORT_EMAIL }}</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { accountBaseUrlFromHost, tenantAccessEnded } from '../utils/tenantAvailability';
import { LumiIcon } from '@lumi/ui';

// The page a person gets when the workspace they opened does not exist, or can
// no longer be reached (issue 364, F57). It replaces the WHOLE app, not one
// view: the gate is on reachability, so every route behind it has nothing to
// show — the same reasoning portal-ui applies to a dark career portal.
//
// What it must NOT carry: a workspace name, a slug, an address of anyone in it,
// or any hint of WHY. Every module API answers "suspended" and "never existed"
// identically on purpose, and this page is the visible half of that decision —
// naming either cause would confirm to a stranger which workspaces exist.
//
// The one variant (issue 669): when the cause is the SIGNED-IN person's own lost
// membership — user-api / ats-api answered 403 TENANT_MEMBERSHIP_DENIED, e.g. a
// demo guest revoked, or the demo ended under an open tab — the page says
// "you don't have access to this workspace": signed in, but not (or no longer) a
// member, and a demo guest's demo has ended or the invitation was withdrawn. One
// wording that is true for a revoked guest and equally for someone who never was
// a member. It names no workspace and no other person, and the caller was just
// told 403 for exactly this fact.
//
// Two ways out, both of which work for someone who cannot get in:
//  · `account.{LUMI_DOMAIN}` — the tenant-less data-subject host, where the
//    Art. 15(3) takeout, the erasure request and the invoices of a suspended
//    workspace live (ARCHITECTURE.md). Injected by the app when it has resolved
//    LUMI_DOMAIN, else derived from the current host; app-kit owns no runtime
//    config and no domain literal (docs/operations/environments.md).
//  · the PUBLIC support address. Deliberately not the in-app ticket dialog:
//    support-api is tenant-scoped, so it refuses exactly the person reading
//    this. Same inbox the product already publishes (landing-ui's SharedFooter,
//    workspace-ui's WorkspaceLocked) — an address, not translatable copy, so it
//    lives here rather than in i18n. It is Lumi's own mailbox and confirms
//    nothing about any workspace.
const props = defineProps<{ accountUrl?: string }>();

const SUPPORT_EMAIL = 'support@lumi-hr.de';

const { t } = useI18n();
const accessEnded = computed(() => tenantAccessEnded.value);
const accountHref = computed(() => String(props.accountUrl || accountBaseUrlFromHost() || '').replace(/\/+$/, ''));
const supportMailto = `mailto:${SUPPORT_EMAIL}`;
</script>

<style scoped>
.tenant-gone {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px 16px;
  background: var(--bg-base, var(--surface-page, transparent));
}
.tenant-gone__card {
  max-width: 520px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: var(--surface-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  padding: 40px 32px;
  box-shadow: var(--shadow-md);
}
.tenant-gone__icon {
  width: 44px;
  height: 44px;
  color: var(--text-muted);
}
.tenant-gone__title {
  font-size: var(--text-2xl);
  font-weight: 600;
  color: var(--text-heading);
}
.tenant-gone__body {
  color: var(--text-muted);
  line-height: 1.5;
}
.tenant-gone__lead {
  color: var(--text-body);
  margin-top: 8px;
}
.tenant-gone__cta {
  display: inline-block;
  padding: 10px 20px;
  border-radius: var(--radius-md);
  background: var(--accent-soft);
  color: var(--accent-text);
  font-weight: 600;
  text-decoration: none;
}
.tenant-gone__cta:hover {
  background: var(--accent-soft-2);
}
.tenant-gone__link {
  font-size: var(--text-sm);
  color: var(--text-muted);
  text-decoration: underline;
}
.tenant-gone__email {
  font-size: var(--text-sm);
  color: var(--text-muted);
  text-decoration: underline;
}
</style>
